import React from 'react'
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Typography, Box, Chip, Link, IconButton, Tooltip
} from '@mui/material'
import { FlagsIconSeeker, IconSeeker } from '@ui-seeker/core'

const getAlertColor = (level) => {
    switch (level) {
        case 'critical': return 'error'
        case 'warning': return 'warning'
        default: return 'success'
    }
}

const getAlertIcon = (level) => {
    switch (level) {
        case 'critical': return '🔴'
        case 'warning': return '🟡'
        default: return '🟢'
    }
}

export default function PriceAlertsTable({ alerts }) {
    if (!alerts || alerts.length === 0) {
        return (
            <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    No price data available for the selected date range
                </Typography>
            </Paper>
        )
    }

    return (
        <TableContainer component={Paper} variant="outlined">
            <Table size="small">
                <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell>Market</TableCell>
                        <TableCell align="center">Sites</TableCell>
                        <TableCell>Min Price</TableCell>
                        <TableCell>Max Price</TableCell>
                        <TableCell align="center">Difference</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {alerts.map((alert, index) => (
                        <TableRow
                            key={index}
                            sx={{
                                bgcolor: alert.alert_level === 'critical' ? 'error.lighter' :
                                         alert.alert_level === 'warning' ? 'warning.lighter' : 'inherit'
                            }}
                        >
                            <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <FlagsIconSeeker countryName={alert.market} />
                                    <Typography variant="body2" fontWeight="medium">
                                        {alert.market}
                                    </Typography>
                                </Box>
                            </TableCell>
                            <TableCell align="center">
                                <Chip label={alert.sites_count} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box>
                                        <Typography variant="body2" color="success.main" fontWeight="medium">
                                            {alert.currency}{alert.min_price.toFixed(2)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 100, display: 'block' }}>
                                            {alert.min_site}
                                        </Typography>
                                    </Box>
                                    {alert.min_url && (
                                        <Tooltip title="Open product">
                                            <IconButton
                                                size="small"
                                                component={Link}
                                                href={alert.min_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ ml: 0.5 }}
                                            >
                                                <IconSeeker icon="external-link" size="xs" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            </TableCell>
                            <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box>
                                        <Typography variant="body2" color="error.main" fontWeight="medium">
                                            {alert.currency}{alert.max_price.toFixed(2)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 100, display: 'block' }}>
                                            {alert.max_site}
                                        </Typography>
                                    </Box>
                                    {alert.max_url && (
                                        <Tooltip title="Open product">
                                            <IconButton
                                                size="small"
                                                component={Link}
                                                href={alert.max_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ ml: 0.5 }}
                                            >
                                                <IconSeeker icon="external-link" size="xs" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            </TableCell>
                            <TableCell align="center">
                                <Chip
                                    label={`${getAlertIcon(alert.alert_level)} ${alert.diff_pct}%`}
                                    size="small"
                                    color={getAlertColor(alert.alert_level)}
                                    variant={alert.alert_level === 'ok' ? 'outlined' : 'filled'}
                                />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    )
}
